import { request } from 'lib/apiv3/utils/request'
import { loadAllPages } from 'lib/loadAllPages'
import { records } from 'data/v3Records'
import _ from 'lodash'

// These are added to the params whitelist for all endpoints by default, to allow configuring apiv3 collection responses
const COLLECTION_RESPONSE_PARAMS = ['orderBy', 'orderDir', 'perPage', 'defaultPerPage', 'page']
const MAX_PER_PAGE = 200

const snakeCaseKeys = obj => _.mapKeys(obj, (v, k) => _.snakeCase(k))
const camelCaseKeys = obj => _.mapKeys(obj, (v, k) => _.camelCase(k))

/*
 * the v3resources framework makes a few restrictive assumptions in order to give us a bunch
 * of wonderful goodies. there are a number of tricky pitfalls that can come from using the
 * resource pattern in situations where these assumptions do not hold. if you're in such a
 * situation, v3resources may not be the tool for you.
 *
 * the assumptions are the following
 * 1. there is a 1 - 1 - 1 correspondence between `fooResource`, `FooController` and a `foos` db table
 * 2. every endpoint your resource will interact with will respond with an `ApiV3` method
 *   2a. this implies a response shape-- check out `ApiV3.record_with_extras`, `ApiV3.raw_response`,
 *   and `ApiV3.single_error` defined in lib/api_v3.rb to see that
 *   2b. the exception to this rule is `.search`, which hits the `SearchingController` and returns
 *   a legacy response format. this is outstanding work: https://github.com/ridewithgps/ridewithgps/issues/8061
 *
 * additionally, as currently designed and implemented, the apiv3 reducer state is only automatically modified
 * when it sees things that look like registered apiv3 objects come back in responses. put another way,
 * the v3 reducer passively pulls records into state that it recognizes. and it leaves updating and removing
 * those records in/from state to the implementer of a particular resource.
*/

export const createResource = (_records) => {
  // Convenience map to lookup records by string name.
  const nameToRecord = _records.reduce((map, record) => {
    map[_.snakeCase(record.modelName)] = record
    return map
  }, {})

  // Maintains shape of extras ({id, type, [modelName]}), just deserializes the record.
  const deserializeExtras = extras => (
    extras.filter(x => {
      if (nameToRecord[x.type]) {
        return true
      } else {
        console.warn(`unrecognized extra of type ${x.type}. did you forget to register your record in data/v3Records?`)
        return false
      }
    }).map(x => ({ ...x, [x.type]: nameToRecord[x.type].deserialize(x[x.type]) }))
  )

  const deserializeCollectionResponse = ({ results, total_count: totalCount, extras, permissions, meta }) => ({
    results: results.length ? results.map(nameToRecord[meta.type].deserialize) : [],
    totalCount,
    extras: deserializeExtras(extras),
    permissions: camelCaseKeys(permissions),
    meta
  })

  // The only way we have to distinguish a record or raw response, is to hope that the key used
  // in the raw response doesn't exist as a record name. We could add {raw:true} or something to
  // meta if we want to fix this.
  const deserializeRecordOrRawResponse = ({ extras, permissions, meta, ...recordOrRaw }) => {
    const keys = Object.keys(recordOrRaw)
    if (keys.length !== 1) {
      return Promise.reject(new Error('Invalid ApiV3 response: Expected single record or single raw response key.'))
    }
    const permissionsCamelCase = camelCaseKeys(permissions)
    const k = keys[0]
    const maybeDeserialized = nameToRecord[k]
      ? nameToRecord[k].deserialize(recordOrRaw[k]).set('permissions', permissionsCamelCase)
      : recordOrRaw[k]
    return {
      [_.camelCase(k)]: maybeDeserialized,
      extras: deserializeExtras(extras),
      permissions: permissionsCamelCase,
      meta
    }
  }

  // Maintain response shape, just tranform keys to camelcase and deserialize all known records.
  const deserializeResponse = (response) => {
    if (!Array.isArray(response.extras)) {
      return Promise.reject(new Error("Invalid ApiV3 response: Expected 'extras' to be an array"))
    }

    if (response.results) {
      return deserializeCollectionResponse(response)
    } else {
      return deserializeRecordOrRawResponse(response)
    }
  }

  /*
  Creates our member[method] and collection[method] helpers that are just wrappers around apiv3/utils/request

  Differences using this vs. apiv3/utils/request:

  - Deserializes records, camelcases keys: Transforms apiv3 response minimally by camel casing keys and deserializing
    all known record types.

  - Requests built from helpers: Building the request functions is simple. We export member and collection, which
    include keys for each request method (ex. member.post, collection.get). You use these helper functions to
    define the apiv3 interface.

    Examples:
      - POST /xyz/5/disable:
        member.post('disable')
      - GET /xyz/routes?source=pinned:
        collection.get('routes', {source: 'pinned'})
      - PATCH /xyz/5/update_code, with body including {code} var:
        member.patch('updateCode', ['code'])
      - PATCH /xyz/5/code?force_generate=true, but export api.generateCode instead of api.code:
        ['generateCode', member.patch('code', {forceGenerate: true})]

    These examples produce an api object like this:
      - api.disable(id)
      - api.routes()
      - api.updateCode(id, {code})
      - api.generateCode(id)
  */
  /*
   * STRUGGLING WITH ENDPOINTBUILDER?
   * consider reading https://rwgps.slack.com/archives/CBTAWFDJ8/p1572548692176900
   * in which several common gotcha's and failure cases are laid out!
   * also, please increment this counter (started 10/31/19) so we can evaluate
   * the relative priority of refactoring this method
   *
   * HOURS_SPENT_DEBUGGING_ENDPOINTBUILDER = 3
   */
  const endpointBuilder = (method, { isCollection = false, isCreate = false } = {}, format = 'json') => (action, ...rest) => record => {
    const modelNamePlural = record.modelNamePlural || (record.modelName + 's')
    const modelBaseEndpoint = `/${_.snakeCase(modelNamePlural)}`

    let paramsWhitelist = []
    let fixedParams = {}
    if (Array.isArray(rest[0])) {
      paramsWhitelist = rest[0]
      if (typeof rest[1] === 'object') {
        fixedParams = snakeCaseKeys(rest[1])
      }
    } else if (typeof rest[0] === 'object') {
      fixedParams = snakeCaseKeys(rest[0])
      if (Array.isArray(rest[1])) {
        paramsWhitelist = rest[1]
      }
    }

    paramsWhitelist = paramsWhitelist.concat(COLLECTION_RESPONSE_PARAMS, ['extras'], !isCollection ? [record.modelName] : []).map(x => _.snakeCase(x))

    const getPath = id => `${modelBaseEndpoint}${(isCollection || isCreate) ? '' : `/${id}`}${action ? `/${_.snakeCase(action)}` : ''}.${format}`
    const getOpts = (_params) => {
      // special case for multi-part encoded requests, doesn't support fixedParams
      if (typeof _params.append === 'function') {
        return {
          headers: { 'content-type': undefined },
          body: _params
        }
      }

      // if we're being passed attributes, we need to manually snakeCase, since
      // there's no call to record.serialize
      if (_params[record.modelName]) {
        _params[record.modelName] = snakeCaseKeys(_params[record.modelName])
      }

      const params = { ...fixedParams, ..._.pick({ ...snakeCaseKeys(_params) }, paramsWhitelist) }
      if (method === 'get') {
        return { params }
      } else {
        return { body: JSON.stringify(params) }
      }
    }

    const apiRequest = (...args) => {
      let path, opts
      if (isCollection) {
        const params = args[0] || {}
        path = getPath()
        opts = getOpts(params)
      } else {
        const idOrRecord = args[0]
        let params = args[1] || {}
        let id = idOrRecord
        if (typeof idOrRecord === 'object') {
          id = idOrRecord.id
          // TODO: Once we upgrade to immutable v4, we can use isImmutable for this test instead of duck typing
          const serialized = (typeof idOrRecord.toJS === 'function') ? record.serialize(idOrRecord) : idOrRecord

          // NOTE: the below means that we can't POST/PATCH with both
          // fileAttrs AND non-model attributes. only one or the other
          params = serialized instanceof window.FormData
            ? serialized
            : { ...serialized, ...params }
        }
        path = getPath(id)
        opts = getOpts(params)
      }
      return request[method](path, opts)
        .then(response => deserializeResponse(response))
    }
    apiRequest.isCollection = isCollection

    return {
      action,
      request: apiRequest
    }
  }

  const _member = {
    get: endpointBuilder('get'),
    post: endpointBuilder('post'),
    patch: endpointBuilder('patch'),
    delete: endpointBuilder('delete')
  }

  const _collection = {
    get: endpointBuilder('get', { isCollection: true }),
    post: endpointBuilder('post', { isCollection: true }),
    patch: endpointBuilder('patch', { isCollection: true }),
    delete: endpointBuilder('delete', { isCollection: true })
  }

  const crud = [
    ['create', endpointBuilder('post', { isCreate: true })()],
    ['read', _member.get()],
    ['update', _member.patch()],
    ['delete', _member.delete()],
    ['list', _collection.get()]
  ]

  // Used to build the interface described above endpointBuilder
  const apiInterface = (record, endpoints = []) => {
    const iface = {}
    ;[...crud, ...endpoints].forEach(arg => {
      if (Array.isArray(arg)) {
        iface[arg[0]] = arg[1](record).request
      } else {
        const endpoint = arg(record)
        iface[endpoint.action] = endpoint.request
      }
    })
    return iface
  }

  /*
  Takes a record type and return value of apiInterface(...) and returns an object
  with the same keys, but values are action creators. Using the action creators
  instead of the api request methods themselves will ensure the apiv3 reducer can
  store all the records that are returned from the backend and deserialized here.

  Action creators proxy arguments to the api functions directly, so their signature
  is unchanged here. Each action creator is given a `type` property to allow you to
  use the action type without actually creating an action first. This is useful
  when writing custom reducers.
  */
  const apiActions = (record, api) => {
    const actions = {}
    Object.keys(api).forEach(name => {
      const type = `apiv3/${record.modelName}/${name}`
      actions[name] = (...args) => {
        const meta = { apiv3source: record.modelName }
        if (!api[name].isCollection) {
          let id = args[0]
          if (typeof id === 'object') {
            id = id.id
          }
          meta.id = id
        }
        return {
          type,
          payload: api[name](...args),
          meta
        }
      }

      actions[name].type = type
    })
    return actions
  }

  return [_member, _collection, (record, endpoints) => {
    const api = apiInterface(record, endpoints)
    const actions = apiActions(record, api)
    return [api, actions]
  }]
}

/*
This builds the API and action creators for a resource associated with the given record
*/
const [member, collection, resource] = createResource(records)
export { member, collection, resource }

// Default selector that should be mixed in to selectors in order to get lookups by id.
export const findSelector = record => ({
  find: (state, id) => state.apiv3.getIn([record.modelName, 'id', id]),
  findByIds: (state, ids) => ids.map(id => state.apiv3[record.modelName].id.get(id)).filter(v => v)
})

/*
Collection responses are paginated. If you want all results, wrap your action in withAllResults. See user resource for example.
*/
export const withAllResults = action => (id, params) => dispatch => {
  const loadPage = ({ pageIndex: page, perPage }) => (
    dispatch(action(id, { ...params, page, perPage }))
  )
  return loadAllPages({
    promiseProducer: loadPage,
    perPage: 200,
    isLastPage: ({ perPage, pageIndex, pageResults }) => pageResults.value.totalCount <= perPage * (pageIndex + 1)
  })
}

/*
API request helper to load all results for a paginated collection response.
`fetchWithParams` is expected to be a function that takes a params object as its argument
and calls a function produced with `apiInterface` that returns an apiv3 collection response.

Example:

  const api = apiInterface(Experience, [
    member.get('appCodeUses')
  ])
  loadAll(params => api.appCodeUses(5, params))
    .then(({results, extras, permissions}) => { doStuff() })

*/
export const loadAllPagesCombined = fetchWithParams => {
  // these will be concatenated/merged from all requests
  const results = []
  const extras = []
  const permissions = {}

  const promiseProducer = ({ perPage, pageIndex }) => fetchWithParams({ perPage, page: pageIndex }).then(response => {
    // execute side effects
    response.results.forEach(result => { results.push(result) })
    response.extras.forEach(extra => { extras.push(extra) })
    Object.keys(response.permissions).forEach(key => { permissions[key] = response.permissions[key] })
    // return the response so that it can be inspected by isLastPage
    return response
  })

  return loadAllPages({
    promiseProducer,
    perPage: MAX_PER_PAGE,
    isLastPage: ({ perPage, pageIndex, pageResults }) => pageResults.totalCount <= perPage * (pageIndex + 1)
  }).then(() => ({ results, extras, permissions }))
}
